import { ActivationPackage, LegalState } from "./api"

/**
 * Sample state for reviewing the page before the API is deployed.
 *
 * Only reachable from a development build, and the page marks itself as showing
 * sample data when it falls back to this — a review copy that cannot be told
 * apart from real data is worse than no review copy.
 */
export function sampleLegalState(): LegalState {
  const now = Math.floor(Date.now() / 1000)

  return {
    documents: [
      {
        code: "MASTER",
        name: "Master Platform Agreement",
        plainLanguageSummary: "The main agreement covering your use of Travel Code.",
        scope: "account",
        productCode: null,
        requiresSignature: false,
        currentVersion: { id: 1, label: "2026.1", effectiveFrom: "2026-08-01" },
        accepted: {
          versionId: 1, versionLabel: "2026.1", acceptedAt: "2026-08-04 09:12:00",
          userId: 2, signerName: "Egor Karpovich", signerTitle: "CEO", method: "clickwrap", pdfUrl: null,
        },
      },
      {
        code: "AUP",
        name: "Acceptable Use Policy",
        plainLanguageSummary: "What the platform may and may not be used for.",
        scope: "account",
        productCode: null,
        requiresSignature: false,
        currentVersion: { id: 2, label: "2026.1", effectiveFrom: "2026-08-01" },
        accepted: {
          versionId: 2, versionLabel: "2026.1", acceptedAt: "2026-08-04 09:12:00",
          userId: 2, signerName: "Egor Karpovich", signerTitle: "CEO", method: "clickwrap", pdfUrl: null,
        },
      },
      {
        code: "SCH_A",
        name: "Schedule A — Travel Services",
        plainLanguageSummary: "Covers booking travel through us and how airline and hotel rules apply.",
        scope: "product",
        productCode: "TRAVEL",
        requiresSignature: false,
        currentVersion: { id: 5, label: "2026.2", effectiveFrom: "2026-08-20" },
        accepted: {
          versionId: 4, versionLabel: "2026.1", acceptedAt: "2026-08-04 09:13:00",
          userId: 2, signerName: "Egor Karpovich", signerTitle: "CEO", method: "clickwrap", pdfUrl: null,
        },
      },
      {
        code: "SCH_B",
        name: "Schedule B — Expense Management",
        plainLanguageSummary: "Covers expense management, receipt scanning, and connecting your existing cards.",
        scope: "product",
        productCode: "EXPENSE",
        requiresSignature: false,
        currentVersion: { id: 7, label: "2026.1", effectiveFrom: "2026-08-01" },
        accepted: null,
      },
      {
        code: "SO",
        name: "Service Order",
        plainLanguageSummary: "Your pricing and plan.",
        scope: "commercial",
        productCode: null,
        requiresSignature: true,
        currentVersion: { id: 9, label: "SO-2026-014", effectiveFrom: "2026-08-10" },
        accepted: {
          versionId: 9, versionLabel: "SO-2026-014", acceptedAt: "2026-08-10 15:40:00",
          userId: 2, signerName: "Egor Karpovich", signerTitle: "CEO", method: "esignature", pdfUrl: null,
        },
      },
    ],
    activatedProducts: ["TRAVEL", "EXPENSE"],
    consents: [],
    pendingActivations: [{ productCode: "CARD", total: 8, done: 1, complete: false }],
    outstanding: [
      {
        code: "SCH_A", name: "Schedule A — Travel Services", scope: "product", productCode: "TRAVEL",
        changeType: "material",
        summary: "RateGuard now shares recovered fare differences with you, and the refund window for changed bookings is stated explicitly.",
        versionId: 5, firstSeen: false, deadlineTs: now + 11 * 86400, overdue: false,
      },
      {
        code: "SCH_B", name: "Schedule B — Expense Management", scope: "product", productCode: "EXPENSE",
        changeType: "material", summary: null, versionId: 7, firstSeen: true, deadlineTs: null, overdue: true,
      },
    ],
    requiresAction: true,
    nearestDeadline: now + 11 * 86400,
    restriction: { products: ["EXPENSE"], account: false },
    maySign: true,
    signer: { name: "Egor Karpovich", title: "CEO" },
  }
}

/**
 * The card activation package, which is the one flow whose shape is a legal
 * requirement rather than a design choice — our Schedules, then each third-party
 * agreement on its own, then the data-sharing consent last and alone.
 */
export function sampleActivationPackage(): ActivationPackage {
  const doc = (code: string, name: string, summary: string, versionId: number) => ({
    kind: "clickwrap" as const,
    code, name, summary,
    counterparty: null, externalUrl: null,
    versionId, versionLabel: "2026.1",
    affirmation: null, withdrawal: null,
    satisfied: false, blocked: false,
  })

  const party = (code: string, name: string, counterparty: string, url: string, versionId: number) => ({
    kind: "third_party" as const,
    code, name, summary: null,
    counterparty, externalUrl: url,
    versionId, versionLabel: "2026.1",
    affirmation: null, withdrawal: null,
    satisfied: false, blocked: false,
  })

  return {
    productCode: "CARD",
    steps: [
      { ...doc("SCH_C", "Schedule C — Card Program", "Covers how company cards work, limits, and disputes.", 21), satisfied: true },
      doc("TC_CASH", "TC Cash Program Terms", "Covers how you earn and spend TC Cash.", 22),
      party("PLIANT_GTC", "Platform General Terms", "Pliant GmbH", "https://getpliant.com/terms", 23),
      party("PLIANT_PAYMENTS", "Payments Agreement", "Pliant GmbH", "https://getpliant.com/terms", 24),
      party("PLIANT_CARD", "Payment Card Agreement", "Pliant GmbH", "https://getpliant.com/terms", 25),
      party("PLIANT_PRIVACY", "Privacy Policy", "Pliant GmbH", "https://getpliant.com/privacy", 26),
      party("ISSUER_CARDHOLDER", "Cardholder Agreement", "the issuing bank", "https://getpliant.com/terms", 27),
      {
        kind: "consent" as const,
        code: "CARD_API_DATA_SHARING",
        name: "Sharing your data with the card issuer",
        summary:
          "Before we can connect the card programme we need your separate permission to exchange API "
          + "access credentials and tokens with our card partner and the issuing bank, and to show your "
          + "card and account information inside Travel Code.",
        counterparty: null, externalUrl: null,
        versionId: null, versionLabel: null,
        affirmation:
          "I consent to Travel Code sharing the data described above with the card partner and the issuing bank.",
        withdrawal:
          "You can withdraw this permission at any time. Withdrawing it ends the card programme and "
          + "Schedule C, and leaves the rest of your agreement in force.",
        satisfied: false, blocked: false,
      },
    ],
    complete: false,
    blocked: [],
    consentScreenVersion: "2026.1",
    maySign: true,
  }
}

/** A short readable stand-in for the document text in the viewer. */
export function sampleDocumentBody(name: string): string {
  return [
    `<h2>${name}</h2>`,
    "<p>This is sample text shown while the documents API is not yet deployed.</p>",
    "<p>The real viewer shows the customer's own accepted copy when there is one, and the current published version otherwise, so what is read here is the text an acceptance is bound to.</p>",
  ].join("\n")
}
